<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';

import InformationTab from './tabs/InformationTab.vue';
import StagesTab from './tabs/StagesTab.vue';
import PermissionsTab from './tabs/PermissionsTab.vue';
import DeleteTab from './tabs/DeleteTab.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();

const pipelineId = computed(() => Number(route.params.pipelineId));
const accountId = computed(() => store.getters.getCurrentAccountId);
const isLoading = ref(false);

const pipeline = computed(() =>
  store.getters['pipelines/getPipeline'](pipelineId.value)
);

const activeTabIndex = ref(0);

const tabs = [
  { label: 'Informacoes' },
  { label: 'Etapas' },
  { label: 'Permissoes' },
  { label: 'Excluir' },
];

const tabKeys = ['information', 'stages', 'permissions', 'delete'];

const activeTabKey = computed(() => tabKeys[activeTabIndex.value]);

const handleTabChange = tab => {
  activeTabIndex.value = tabs.findIndex(t => t.label === tab.label);
};

const goBack = () => {
  router.push(`/app/accounts/${accountId.value}/pipelines`);
};

onMounted(async () => {
  if (!pipeline.value || !pipeline.value.id) {
    isLoading.value = true;
    try {
      await store.dispatch('pipelines/show', {
        accountId: accountId.value,
        pipelineId: pipelineId.value,
      });
    } finally {
      isLoading.value = false;
    }
  }
});
</script>

<template>
  <div class="w-full p-6">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div class="flex items-center gap-3">
        <Button
          variant="ghost"
          icon="i-lucide-arrow-left"
          size="sm"
          @click="goBack"
        />
        <div>
          <h1 class="text-2xl font-bold text-n-slate-12 m-0">
            {{ pipeline?.name || 'Carregando...' }}
          </h1>
          <p class="text-sm text-n-slate-11 m-0">
            Configuracoes do Pipeline
          </p>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="flex items-center justify-center py-20">
      <Spinner />
    </div>

    <!-- Content -->
    <div v-else-if="pipeline && pipeline.id" class="space-y-6">
      <!-- TabBar -->
      <TabBar
        :tabs="tabs"
        :initial-active-tab="activeTabIndex"
        class="max-w-xl"
        @tab-changed="handleTabChange"
      />

      <!-- Tab Content -->
      <div class="bg-n-solid-1 border border-n-weak rounded-lg p-6">
        <InformationTab
          v-if="activeTabKey === 'information'"
          :pipeline="pipeline"
        />

        <StagesTab
          v-if="activeTabKey === 'stages'"
          :pipeline="pipeline"
        />

        <PermissionsTab
          v-if="activeTabKey === 'permissions'"
          :pipeline="pipeline"
        />

        <DeleteTab
          v-if="activeTabKey === 'delete'"
          :pipeline="pipeline"
          @deleted="goBack"
        />
      </div>
    </div>

    <!-- Not Found -->
    <div v-else class="flex items-center justify-center py-20">
      <div class="text-center">
        <p class="text-sm text-n-slate-11">Pipeline nao encontrado</p>
        <Button class="mt-4" @click="goBack">
          Voltar
        </Button>
      </div>
    </div>
  </div>
</template>
